import { type Connection, PublicKey, SYSVAR_RENT_PUBKEY } from '@solana/web3.js';
import type BN from 'bn.js';
import { fetchMarket } from '../accounts/fetch.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import {
  CP_SWAP_PROGRAM_ID,
  MORFI_OPINION_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import {
  deriveAmmAuthorityPda,
  deriveAmmConfigPda,
  deriveAmmLpLockPda,
  deriveAmmLpMintPda,
  deriveAmmObservationPda,
  deriveAmmPoolStatePda,
  deriveAmmPoolVaultPda,
  sortMintPair,
} from '../pda/cp-swap.js';
import { deriveTokenMetadataPda } from '../pda/derivations.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import { createBuildResult, invokeMethod } from './common.js';

const MPL_TOKEN_METADATA_PROGRAM_ID: PublicKey = new PublicKey(
  'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s',
);

export type {
  ClaimBackerFeesAccounts,
  ClaimBackerFeesParams,
  ClaimPrizeAccounts,
  ClaimPrizeParams,
} from './claims.js';
export { buildClaimBackerFees, buildClaimPrize } from './claims.js';

export interface GraduateAccounts {
  marketConfig: PublicKey;
  quoteMint: PublicKey;
  nOutcomes: number;
  /** Null for conviction markets; the bound schedule address for timed markets. */
  eventSchedule: PublicKey | null;
}

export interface GraduateParams extends BuildParams {
  cranker: PublicKey;
  market: PublicKey;
  /** Candidate checked by the contract's resolution policy, before any final winner exists. */
  candidateWinner: number;
  /** Earliest conviction touch; null is required for timed markets. */
  firstTouchTs: BN | null;
  /** Indexed market facts bypass the existing public Market read. */
  graduateAccounts?: GraduateAccounts;
}

/** Build atomic resolution and protected-pool migration; amounts remain contract-owned. */
export async function buildGraduate(
  connection: Connection,
  params: GraduateParams,
): Promise<BuildResult> {
  const winner = params.candidateWinner;
  if (!Number.isInteger(winner) || winner < 0 || winner > 255) {
    throw new Error('candidateWinner must fit in u8');
  }
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const payer = params.cranker;
  const accounts =
    params.graduateAccounts ?? (await loadGraduateAccounts(connection, params.market, programId));
  if (winner >= accounts.nOutcomes)
    throw new Error('candidateWinner is outside market outcome count');
  const winningOutcomeMint = marketPda(programId, 'outcome_mint', params.market, winner);
  const ammConfig = deriveAmmConfigPda(CP_SWAP_PROGRAM_ID);
  const [mint0, mint1] = sortMintPair(accounts.quoteMint, winningOutcomeMint);
  const poolState = deriveAmmPoolStatePda(ammConfig, mint0, mint1, CP_SWAP_PROGRAM_ID);
  const program = await getProgram(connection, programId);
  const instruction = await invokeMethod(
    program,
    'resolveDrainAndMigrate',
    winner,
    params.firstTouchTs,
  )
    .accounts({
      payer,
      market: params.market,
      marketConfig: accounts.marketConfig,
      quoteMint: accounts.quoteMint,
      winningOutcomeMint,
      outcomeMintAuthority: marketPda(programId, 'outcome_mint_authority', params.market),
      winnerMetadata: deriveTokenMetadataPda(winningOutcomeMint)[0],
      marketQuoteVaultAuthority: marketPda(
        programId,
        'market_quote_vault_authority',
        params.market,
      ),
      marketQuoteVault: marketPda(programId, 'market_quote_vault', params.market),
      winnerTokenVault: marketPda(programId, 'outcome_token_vault', params.market, winner),
      tokenMetadataProgram: MPL_TOKEN_METADATA_PROGRAM_ID,
      morfiAmmProgram: CP_SWAP_PROGRAM_ID,
      morfiAmmConfig: ammConfig,
      cpAuthority: deriveAmmAuthorityPda(CP_SWAP_PROGRAM_ID),
      poolState,
      lpMint: deriveAmmLpMintPda(poolState, CP_SWAP_PROGRAM_ID),
      lpLock: deriveAmmLpLockPda(poolState, CP_SWAP_PROGRAM_ID),
      token0Vault: deriveAmmPoolVaultPda(poolState, mint0, CP_SWAP_PROGRAM_ID),
      token1Vault: deriveAmmPoolVaultPda(poolState, mint1, CP_SWAP_PROGRAM_ID),
      observationState: deriveAmmObservationPda(poolState, CP_SWAP_PROGRAM_ID),
      cpEventAuthority: marketPda(CP_SWAP_PROGRAM_ID, '__event_authority'),
      tokenProgram: TOKEN_PROGRAM_ID,
      systemProgram: SYSTEM_PROGRAM_ID,
      rent: SYSVAR_RENT_PUBKEY,
      winningClaimRightsMint: marketPda(programId, 'claim_rights_mint', params.market, winner),
      eventSchedule: accounts.eventSchedule,
      eventAuthority: marketPda(programId, '__event_authority'),
      program: programId,
    })
    .instruction();
  return createBuildResult(
    [instruction],
    [payer],
    params.computeUnits ?? estimateComputeUnits('resolve_drain_and_migrate'),
  );
}

async function loadGraduateAccounts(
  connection: Connection,
  address: PublicKey,
  programId: PublicKey,
): Promise<GraduateAccounts> {
  const market = await fetchMarket(connection, address, programId);
  return {
    marketConfig: market.config,
    quoteMint: market.quoteMint,
    nOutcomes: market.nOutcomes,
    eventSchedule: market.eventSchedule.equals(PublicKey.default) ? null : market.eventSchedule,
  };
}

function marketPda(
  programId: PublicKey,
  seed: string,
  market?: PublicKey,
  outcome?: number,
): PublicKey {
  const seeds: Buffer[] = [Buffer.from(seed)];
  if (market) seeds.push(market.toBuffer());
  if (outcome !== undefined) seeds.push(Buffer.from([outcome]));
  return PublicKey.findProgramAddressSync(seeds, programId)[0];
}
